import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    Snackbar,
    Typography,
    Box,
    CircularProgress,
} from '@mui/material';
import { isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { toDate } from '../../../utils/dateHelpers';
import { useEffect, useState } from 'react';
import { isDefaultDate } from '../../../utils/common';
import { AllAlerts } from '../../../Models/HIE/AllAlerts.model';
import { HIEAlertTypeEnum } from '../../../Enum/HIEAlertTypeEnum';
import { ICareTeam } from '../../../Models/CareTeams/CareTeam.model';
import IRole from '../../../Models/Role/Role.model';

const validateDate = (date) => {
    const d = toDate(date);
    return isValid(d) ? formatInTimeZone(d, 'UTC', 'MM/dd/yyyy') : 'N/A';
};

const validateTime = (time) => {
    return isDefaultDate(time) ? 'N/A' : new Date(time).toLocaleTimeString();
};

export const noteFormatter = (alert: AllAlerts) => {
    return alert ? (
        <>
            <Typography component="div">
                {alert.LastName}, {alert.FirstName} - (DOB {alert.DOB}) -{' '}
                <span style={{ fontWeight: 600 }}>
                    {HIEAlertTypeEnum[alert.HIEAlertType]} Alert --- {alert.VisitType}
                </span>
                .
            </Typography>
            {alert.CHPDownloadDate && <Typography variant="body2">Alert Download Date: {new Date(alert.CHPDownloadDate).toLocaleDateString()}</Typography>}
            {alert.MemberAHCCCS && <Typography variant="body2">AHCCCS ID: {alert.MemberAHCCCS}</Typography>}
            {alert.SendingFacility && <Typography variant="body2">Sending Facility: {alert.SendingFacility}</Typography>}
            {alert.ServicingProviderAddress && <Typography variant="body2">Sending Facility Address: {alert.ServicingProviderAddress}</Typography>}
            {alert.VisitType && <Typography variant="body2">Visit Type: {alert.VisitType}</Typography>}
            {alert.VisitStatus && <Typography variant="body2">Visit Status: {alert.VisitStatus}</Typography>}
            {alert.PCP && <Typography variant="body2">PCP: {alert.PCP}</Typography>}
            {alert.Diagnosis && alert.Diagnosis !== 'null' && <Typography variant="body2">Diagnosis: {alert.Diagnosis}</Typography>}
            {alert.Location && <Typography variant="body2">Location: {alert.Location}</Typography>}
            {alert.AdmitReason && <Typography variant="body2">Admit Reason: {alert.AdmitReason}</Typography>}
            {alert.TestPerformed && <Typography variant="body2">Test Performed: {alert.TestPerformed}</Typography>}
            {alert.Result && <Typography variant="body2">Result: {alert.Result}</Typography>}
            {alert.TestOrdered && <Typography variant="body2">Test Ordered: {alert.TestOrdered}</Typography>}
            {alert.LabOrderResult && <Typography variant="body2">Test Result: {alert.LabOrderResult}</Typography>}
            {alert.ObservationLabRequestDate && <Typography variant="body2">Ordered Date: {alert.ObservationLabRequestDate}</Typography>}
            {alert.ObservationLabDate && <Typography variant="body2">Results Date: {alert.ObservationLabDate}</Typography>}
            {alert.AdmitDate && (
                <Typography variant="body2">
                    Admitted: {validateDate(new Date(alert.AdmitDate))} @ {validateTime(new Date(alert.AdmitTime))}
                </Typography>
            )}
            {alert.DischargeDate && (
                <Typography variant="body2" marginBottom={2}>
                    Discharged: {validateDate(new Date(alert.DischargeDate))} @ {validateTime(new Date(alert.DischargeTime))}
                </Typography>
            )}
            {alert.TestPerformed && <Typography variant="body2">Test Performed: {alert.TestPerformed}</Typography>}
        </>
    ) : null;
};

const noteFormatterClipboard = (alert: AllAlerts) => {
    return (
        `${alert.LastName}, ${alert.FirstName} - (DOB ${alert.DOB}) - ${HIEAlertTypeEnum[alert.HIEAlertType]} Alert.` +
        `${alert.CHPDownloadDate !== null ? `\n\tAlert Download Date: ${new Date(alert.CHPDownloadDate).toLocaleDateString()}` : ''}` +
        `${alert.MemberAHCCCS !== null ? `\n\tMember AHCCCS: ${alert.MemberAHCCCS}` : ''}` +
        `${alert.SendingFacility !== null ? `\n\tSending Facility: ${alert.SendingFacility}` : ''}` +
        `${alert.ServicingProviderAddress !== null ? `\n\tSending Facility Address: ${alert.ServicingProviderAddress}` : ''}` +
        `${alert.VisitType !== null ? `\n\tVisit Type: ${alert.VisitType}` : ''}` +
        `${alert.VisitStatus !== null ? `\n\tVisit Status: ${alert.VisitStatus}` : ''}` +
        `${alert.PCP !== null ? `\n\tPCP: ${alert.PCP}` : ''}` +
        `${alert.Diagnosis && alert.Diagnosis !== 'null' ? `\n\tDiagnosis: ${alert.Diagnosis}` : ''}` +
        `${alert.Location !== null ? `\n\tLocation: ${alert.Location}` : ''}` +
        `${alert.AdmitReason !== null ? `\n\tAdmit Reason: ${alert.AdmitReason}` : ''}` +
        `${alert.TestOrdered !== null ? `\n\tTest Ordered: ${alert.TestOrdered}` : ''}` +
        `${alert.TestPerformed !== null ? `\n\tTest Performed: ${alert.TestPerformed}` : ''}` +
        `${alert.ObservationLabRequestDate !== null ? `\n\tTest Order Date: ${alert.ObservationLabRequestDate}` : ''}` +
        `${alert.ObservationLabDate !== null ? `\n\tTest Result Date: ${alert.ObservationLabDate}` : ''}` +
        `${alert.Result !== null ? `\n\tResult: ${alert.Result}` : ''}` +
        `${alert.AdmitDate !== null ? `\n\tAdmitted: ${validateDate(new Date(alert.AdmitDate))} @ ${validateTime(new Date(alert.AdmitTime))}` : ''}` +
        `${
            alert.DischargeDate !== null
                ? `\n\tDischarged: ${validateDate(new Date(alert.DischargeDate))} @ ${validateTime(new Date(alert.DischargeTime))}`
                : ''
        }\n\n`
    );
};

interface TeamNotificationModalProps {
    alertsList: AllAlerts[];
    selectedMember: any;
    memberCareTeam: ICareTeam | null | undefined;
    roles: IRole[];
    handleCancel: () => void;
    handleMatch: () => void;
    open: boolean;
}

const TeamNotificationModal = (props: TeamNotificationModalProps) => {
    const { alertsList, selectedMember, memberCareTeam, roles, handleCancel, handleMatch, open } = props;
    const [copiedNotes, setCopiedNotes] = useState<AllAlerts[]>([]);
    const [openAlert, setOpenAlert] = useState(false);

    const handleCloseAlert = () => {
        setOpenAlert(false);
    };

    const copiedNotesHandler = (alert) => {
        const alertIds = copiedNotes.map((a) => a.Id);
        let alertsCopy = [...copiedNotes];
        if (alertIds.includes(alert)) {
            alertsCopy = alertsCopy.filter((a) => a.Id !== alert);
        } else {
            alertsCopy = [...copiedNotes, alertsList.find((a) => a.Id === alert)];
        }
        setCopiedNotes(alertsCopy);
    };

    const copyToClipboardHandler = () => {
        let tmp = '';
        for (let i of copiedNotes) {
            tmp += noteFormatterClipboard(i);
        }
        navigator.clipboard.writeText(tmp);
        setOpenAlert(true);
    };

    const roleMap =
        roles?.reduce((map, role) => {
            map[role.Id] = role.Name;
            return map;
        }, {} as Record<string, string>) || {};

    useEffect(() => {
        if (alertsList?.length > 0) {
            setCopiedNotes([...alertsList]);
        }
    }, [alertsList]);

    return (
        <Dialog onClose={handleCancel} open={open} sx={{ padding: 5 }} maxWidth="md" fullWidth>
            <Snackbar open={openAlert} autoHideDuration={2500} onClose={handleCloseAlert} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert variant="filled" onClose={handleCloseAlert} severity={'success'} sx={{ width: '100%' }}>
                    Copied to Clipboard
                </Alert>
            </Snackbar>
            <DialogTitle sx={{ paddingBottom: 1 }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    Team Notification
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ marginTop: 0.5 }}>
                    {`${selectedMember?.FirstName} ${selectedMember?.LastName}`}
                </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', marginBottom: 1.5 }}>
                            Care Team Providers
                        </Typography>

                        {memberCareTeam === undefined ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : !memberCareTeam || (memberCareTeam.TeamRoles?.length === 0 && memberCareTeam.TeamRoleAlternates?.length === 0) ? (
                            <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography color="textSecondary" align="center">
                                    No team members assigned to this member.
                                </Typography>
                            </Paper>
                        ) : (
                            <Paper elevation={0} sx={{ padding: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                {memberCareTeam.TeamRoles && memberCareTeam.TeamRoles.length > 0 && (
                                    <>
                                        <Typography
                                            variant="caption"
                                            sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', display: 'block', marginBottom: 1 }}
                                        >
                                            Primary Team
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {memberCareTeam.TeamRoles.map((teamRole, index) => {
                                                const roleName = roleMap[teamRole.RoleId] || 'Unknown Role';
                                                const providerName = teamRole.ProviderName || '(Unassigned)';

                                                return (
                                                    <Grid size={6} key={`primary-${teamRole.RoleId}-${index}`}>
                                                        <Box sx={{ paddingY: 0.5 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                                                                {providerName}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                                                                {roleName}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </>
                                )}

                                {memberCareTeam.TeamRoleAlternates &&
                                    memberCareTeam.TeamRoleAlternates.filter((alt) => alt.MemberId === selectedMember?.Id).length > 0 && (
                                        <>
                                            <Divider sx={{ marginY: 2 }} />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.secondary',
                                                    textTransform: 'uppercase',
                                                    display: 'block',
                                                    marginBottom: 1,
                                                }}
                                            >
                                                Alternate Providers
                                            </Typography>
                                            <Grid container spacing={1}>
                                                {memberCareTeam.TeamRoleAlternates.filter((alt) => alt.MemberId === selectedMember?.Id).map(
                                                    (alternate, index) => {
                                                        const roleName = roleMap[alternate.RoleId] || 'Unknown Role';
                                                        const providerName = alternate.ProviderName || '(Unassigned)';

                                                        return (
                                                            <Grid size={6} key={`alternate-${alternate.RoleId}-${index}`}>
                                                                <Box sx={{ paddingY: 0.5 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}
                                                                    >
                                                                        {providerName}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                                                                        {roleName} (Alternate)
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        );
                                                    }
                                                )}
                                            </Grid>
                                        </>
                                    )}
                            </Paper>
                        )}
                    </Grid>

                    <Grid size={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, marginTop: 2, marginBottom: 1.5, color: 'text.primary' }}>
                            Alert Details to Copy
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{ padding: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0', maxHeight: '300px', overflowY: 'auto' }}
                        >
                            {!alertsList || alertsList.length === 0 ? (
                                <Typography color="textSecondary" align="center" sx={{ padding: 2 }}>
                                    No alerts selected. Please select alerts from the grid before opening this modal.
                                </Typography>
                            ) : (
                                alertsList.map((alert, index) => (
                                    <Box
                                        key={alert.Id}
                                        sx={{
                                            borderBottom: index < alertsList.length - 1 ? '1px solid #e0e0e0' : 'none',
                                            paddingY: 1.5,
                                        }}
                                    >
                                        <FormControlLabel
                                            label={noteFormatter(alert)}
                                            control={
                                                <Checkbox
                                                    checked={copiedNotes.includes(alert)}
                                                    value={alert}
                                                    id={alert.Id}
                                                    onChange={() => copiedNotesHandler(alert.Id)}
                                                    size="small"
                                                />
                                            }
                                            sx={{ margin: 0, alignItems: 'flex-start' }}
                                        />
                                    </Box>
                                ))
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button
                    disabled={copiedNotes.length === 0}
                    className="button-120"
                    variant="outlined"
                    sx={{ marginRight: 'auto' }}
                    onClick={copyToClipboardHandler}
                >
                    Copy to Clipboard
                </Button>
                <Button className="button-120" variant="contained" onClick={handleMatch}>
                    Match Alert&#40;s&#41;
                </Button>
                <Button className="button-120" variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
export default TeamNotificationModal;
